import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import styled from "styled-components";

import Colors from "../../../shared/colors";
import {
  getFirstNumberFromString,
  handleCatch,
  isDispensingMoreThan3DaysBetween2Dates,
  isDispensingMoreThan7DaysBetween2Dates,
  isObject,
  prescriberName,
} from "../../../shared/helpers";
import moment from "moment";
import CalendarComponent from "../Calendar/Calendar";
import { ReactComponent as DropdownArrowDown } from "../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../assets/icons/dropdownArrowUp.svg";
import useRoles from "../../../hooks/useRoles";
import EditCapsuleCycle from "./EditCapsuleCycle";
import EditCapsuleStrength from "./EditCapsuleStrength";
import APIVariables from "../../../services/api-variables";
import Axios from "../../../services/Axios";
import { ToolTipHover } from "./ToolTipHover";

const Layout = styled.div`
  background-color: white;
  padding: 10px;
  .error {
    color: ${Colors.valencia}!important;
  }
  .patients-wrapper-container {
    .row {
      padding: 7px 0px;
    }
    .col {
      padding-left: 0;
      padding-right: 0;
    }

    label {
      color: ${Colors.orient};
    }
  }
  .table-container {
    display: flex;
    justify-content: center;
    width: 100%;
  }
  table {
    width: 80%;
  }

  td,
  th {
    border: 1px solid ${Colors.alto};
    text-align: center;
    padding: 8px;
    font-weight: normal;

    @media screen and (max-width: 850px) {
      padding: 6px;
    }
  }

  td:first-child,
  th:first-child {
    text-align: left;
    color: ${Colors.orient};
  }

  .container {
    padding-left: var(--bs-gutter-x, 0.75rem);
    padding-right: var(--bs-gutter-x, 0.75rem);
  }

  .edit-option {
    margin-left: 10px;
    color: #e41818;
    text-decoration: underline;
    cursor: pointer;
  }
  .custom-select__control {
    width: 75%;
  }

  .custom-select__menu {
    width: 75%;
  }
  .selectWidth {
    width: 95%;
  }

  .mt-2 {
    input {
      margin-top: 10px;
      margin-bottom: 15px;
      background-color: #f8f8f8;
      color: ${Colors.doveGray} !important;
      border: 0;
      border-bottom: 1px solid ${Colors.silver};
      font-size: 20px;
      line-height: 22px;
      height: 30px;
    }
  }

  .tool-tip-wrapper-capsule-strength {
    min-width: 44px;
  }
  .tool-tip-wrapper-capsule-per-cycle {
    min-width: 85px;
  }
  .display-flex-tooltip {
    display: flex;
  }
`;

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <DropdownArrowUp />
        ) : (
          <DropdownArrowDown />
        )}
      </components.DropdownIndicator>
    )
  );
};

const ViewPAF = ({
  receivedForm,
  handleChangeSubmitedForm,
  numberOfCylesValues,
  isFormApproved,
  isCreateNewPAF,
  handleAditionalOptionsChange,
  aditionalOptions,
  typeOfPacks,
  aditionalOptionsState,
  setFormItems,
  dispensingForm,
  deletePropertyValue,
  setCapsule_changes_tracker,
  capsule_changes_tracker,
  setAditionalOptions,
}) => {
  const [form, setForm] = useState({});
  const [isLastPregancyTestActive, setIsLastPregancyTestActive] =
    useState(false);
  const [isPrescribedOn, setIsPrescribedOn] = useState(false);
  const [isNumberOfCyles, setIsNumberOfCycles] = useState(false);
  const [isFirst, setIsFirst] = useState();
  const {
    isPharmacy,
    isPrescriber,
    isConnectedNurse,
    isHomecare,
    isOutpatient,
    storageData,
  } = useRoles();
  const [capsulePerCycle, setCapsPerCycle] = useState([]);
  const customStyles = {
    singleValue: (provided) => ({
      ...provided,
      color: `${Colors.orient} !important`,
    }),
    option: (provided, { isFocused }) => ({
      ...provided,
      color: `${Colors.orient} !important`,
      top: "-30px !important",
      background: isFocused
        ? `${Colors.porcelain} !important`
        : "white !important",
    }),
  };
  const [editCapsule, setEditCapsule] = useState({});
  const [editedCapsStrength, setEditedCapsStrength] = useState({});

  const is_form_ready_form_approve =
    isPharmacy &&
    (form?.status === "Submitted" ||
      form?.status === "Overdue" ||
      form?.status === "Expiring");

  const isPatientInitiationOrRenewPAF = useMemo(() => {
    return (
      window.location.pathname.includes("patient-initiation") ||
      window.location.pathname.includes("renew")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    if (
      (isPharmacy || isHomecare || isOutpatient) &&
      !disableAditionalOptions()
    ) {
      setAditionalOptions({
        ...aditionalOptions,
        nhs_patient: false,
        private_patient: false,
        number_of_packs: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  const renameKeys = async (toBeRenamed, addNoneOption = false) => {
    await toBeRenamed.forEach((elem) => {
      elem["label"] = elem.value;
      elem["value"] = elem.id;
      delete elem["id"];
    });
    if (addNoneOption) {
      toBeRenamed.unshift({ label: "None", value: null });
    }
    return toBeRenamed;
  };

  useEffect(() => {
    totalCapsulesFirst();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    if (isPharmacy || isPrescriber) {
      let dropDownValues;

      dropDownValues = await Axios.get(
        APIVariables.PRESCRIPTIONS_CREATE_POST
      ).catch((error) => {
        handleCatch(error);
      });

      const { capsule_cycle } = await dropDownValues?.data;

      setCapsPerCycle(await renameKeys(capsule_cycle, false));
    }
  }

  useEffect(() => {
    if (!isCreateNewPAF && isPharmacy && is_form_ready_form_approve) {
      const capsule_changes_tracker = [
        {
          capsule_strength_view_initial: "Incomplete",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: "Incomplete",
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "2_5",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_2_5
            ? receivedForm.capsule_cycle_other_2_5?.toString()
            : receivedForm.capsule_cycle_2_5?.split(" ")[0],

          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "5",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_5
            ? receivedForm.capsule_cycle_other_5?.toString()
            : receivedForm.capsule_cycle_5?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "7_5",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_7_5
            ? receivedForm.capsule_cycle_other_7_5.toString()
            : receivedForm.capsule_cycle_7_5?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "10",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_10
            ? receivedForm.capsule_cycle_other_10?.toString()
            : receivedForm.capsule_cycle_10?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "15",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_15
            ? receivedForm.capsule_cycle_other_15?.toString()
            : receivedForm.capsule_cycle_15?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "20",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_20
            ? receivedForm.capsule_cycle_other_20?.toString()
            : receivedForm.capsule_cycle_20?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
        {
          capsule_strength_view_initial: "25",
          capsule_strength_view_new: "",
          capsule_cycle_view_initial: receivedForm.capsule_cycle_other_25
            ? receivedForm.capsule_cycle_other_25?.toString()
            : receivedForm.capsule_cycle_25?.split(" ")[0],
          capsule_cycle_view_new: "",
        },
      ];

      const history = {
        old_preganacy_test: receivedForm.pregnancy_test,
        old_n_cycles: receivedForm.n_cycles,
        capsule_changes_tracker,
      };

      setCapsule_changes_tracker(history);
      // console.log(capsule_changes_tracker, "trackerSetUp");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_form_ready_form_approve]);

  useEffect(() => {
    setForm({ ...receivedForm });
    // setEditCapsule({});
    setEditedCapsStrength({});
    setIsLastPregancyTestActive(false);
    setIsPrescribedOn(false);
    setIsNumberOfCycles(false);
  }, [receivedForm]);

  const calculateTotalCaps = (formCopy) => {
    const {
      capsule_cycle_2_5,
      capsule_cycle_5,
      capsule_cycle_7_5,
      capsule_cycle_10,
      capsule_cycle_15,
      capsule_cycle_20,
      capsule_cycle_25,
      capsule_cycle_other_2_5,
      capsule_cycle_other_5,
      capsule_cycle_other_7_5,
      capsule_cycle_other_10,
      capsule_cycle_other_15,
      capsule_cycle_other_20,
      capsule_cycle_other_25,
      n_cycles,
    } = formCopy;
    let totalCapsulesPerCycle = 0;

    totalCapsulesPerCycle += !!capsule_cycle_2_5
      ? getFirstNumberFromString(capsule_cycle_2_5)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_2_5
      ? getFirstNumberFromString(+capsule_cycle_other_2_5)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_5
      ? getFirstNumberFromString(capsule_cycle_5)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_5
      ? getFirstNumberFromString(+capsule_cycle_other_5)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_7_5
      ? getFirstNumberFromString(capsule_cycle_7_5)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_7_5
      ? getFirstNumberFromString(+capsule_cycle_other_7_5)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_10
      ? getFirstNumberFromString(capsule_cycle_10)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_10
      ? getFirstNumberFromString(+capsule_cycle_other_10)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_15
      ? getFirstNumberFromString(capsule_cycle_15)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_15
      ? getFirstNumberFromString(+capsule_cycle_other_15)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_20
      ? getFirstNumberFromString(capsule_cycle_20)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_20
      ? getFirstNumberFromString(+capsule_cycle_other_20)
      : 0;

    totalCapsulesPerCycle += !!capsule_cycle_25
      ? getFirstNumberFromString(capsule_cycle_25)
      : 0;
    totalCapsulesPerCycle += !!capsule_cycle_other_25
      ? getFirstNumberFromString(+capsule_cycle_other_25)
      : 0;
    const numberOfCyles =
      typeof n_cycles === "string"
        ? getFirstNumberFromString(n_cycles)
        : n_cycles?.value;
    const totalCapsules = +totalCapsulesPerCycle * numberOfCyles;
    return isNaN(totalCapsules) ? null : totalCapsules;
  };

  const editField = (_, fieldName) => {
    if (fieldName === "lastNegativePregnanacyTest") {
      setIsLastPregancyTestActive(true);
    }

    if (fieldName === "prescribedOn") {
      setIsPrescribedOn(true);
    }

    if (fieldName === "numberOfCycles") {
      setIsNumberOfCycles(true);
    }
  };

  const extractNumberOfCyles = () => {
    if (form.n_cycles) {
      return form.n_cycles;
    } else if (form?.number_cycles?.label) {
      return form?.number_cycles?.label;
    } else {
      return "Incomplete";
    }
  };

  const calculateCapsuleQuantity = (property, otherProperty) => {
    let no_cycles =
      (getFirstNumberFromString(form.n_cycles) ?? form?.number_cycles?.value) ||
      0;

    if (isObject(form[property])) {
      if (form[property].value) {
        return form[property].value * no_cycles;
      }
      return (parseInt(form[otherProperty]) ?? 0) * no_cycles;
    }

    if (!!form[otherProperty]) {
      return (
        getFirstNumberFromString(
          form[otherProperty] * 1 || form[otherProperty]
        ) * no_cycles
      );
    } else if (!!form[property]) {
      return getFirstNumberFromString(form[property]) * no_cycles;
    }
    return 0;
  };

  const displayConditionallyNumberOfCycles = (caps) => {
    // Commented code for working properly on Summary Create a Patient - issue not all items are marked in red
    // if (isFirst === caps) {
    if (isFirst !== caps) {
      return null;
    }

    const is_number_of_cycles_changes = !!capsule_changes_tracker?.n_cycles;

    return (form?.status === "Submitted" ||
      form?.status === "Overdue" ||
      form?.status === "Expiring") &&
      isNumberOfCyles === true &&
      isFormApproved === null ? (
      <Select
        menuShouldScrollIntoView={false}
        classNamePrefix="custom-select"
        components={{
          DropdownIndicator: DropdownIndicator,
          IndicatorSeparator: () => null,
        }}
        value={form?.number_cycles}
        onChange={(selectedOption) => {
          handleChangeSubmitedForm("number_cycles", selectedOption);
        }}
        options={numberOfCylesValues}
        placeholder={form?.n_cycles}
        className="select-border selectWidth "
      />
    ) : (
      <div className="font-size-16 display-flex-tooltip">
        <span
          className={
            "font-size-16 " +
            (checkIfNumberOfCyclesExceeded(form) ||
            extractNumberOfCyles() === "Incomplete"
              ? "red-label"
              : "") +
            (!isCreateNewPAF &&
            !is_form_ready_form_approve &&
            is_number_of_cycles_changes
              ? " green-haze"
              : "")
          }
          style={{ display: "flex", width: "100%" }}
        >
          {extractNumberOfCyles()}
          {!isCreateNewPAF &&
            !is_form_ready_form_approve &&
            is_number_of_cycles_changes && (
              <ToolTipHover
                capsule_changes_tracker={{
                  nr_cyles_view_initial: capsule_changes_tracker?.n_cycles,
                  nr_cyles_view_new: +form?.n_cycles?.split(" Cycle")[0],
                }}
                changedBy={form.approved_or_rejected_by}
                changedOn={form.accepted_date}
                typeOfField="nr_cycles"
              />
            )}
        </span>

        {(form?.status === "Submitted" ||
          form?.status === "Overdue" ||
          form?.status === "Expiring") &&
          isPharmacy && (
            <React.Fragment>
              {isFormApproved === null && isFirst === caps && (
                <span
                  className="edit-option"
                  onClick={(event) => editField(event, "numberOfCycles")}
                >
                  EDIT
                </span>
              )}
            </React.Fragment>
          )}
      </div>
    );
  };

  const totalCapsulesFirst = () => {
    if (!!form?.capsule_cycle_2_5 || form?.capsule_cycle_2_5 === 0) {
      setIsFirst("capsule_cycle_2_5");
    } else if (!!form?.capsule_cycle_5 || form?.capsule_cycle_5 === 0) {
      setIsFirst("capsule_cycle_5");
    } else if (!!form?.capsule_cycle_7_5 || form?.capsule_cycle_7_5 === 0) {
      setIsFirst("capsule_cycle_7_5");
    } else if (!!form?.capsule_cycle_10 || form?.capsule_cycle_10 === 0) {
      setIsFirst("capsule_cycle_10");
    } else if (!!form?.capsule_cycle_15 || form?.capsule_cycle_15 === 0) {
      setIsFirst("capsule_cycle_15");
    } else if (!!form?.capsule_cycle_20 || form?.capsule_cycle_20 === 0) {
      setIsFirst("capsule_cycle_20");
    } else if (!!form?.capsule_cycle_25 || form?.capsule_cycle_25 === 0) {
      setIsFirst("capsule_cycle_25");
    }
  };

  const disableAditionalOptions = () => {
    return (
      form?.status === "Approved" ||
      form?.status === "Rejected" ||
      form?.status === "Dispensed" ||
      !!isFormApproved ||
      !isPharmacy
    );
  };

  const extractIndication = () => {
    if (typeof form.indication === "string") {
      if (form?.indication?.includes("Other")) {
        return `Other (${form?.indication_other})`;
      } else {
        return form.indication;
      }
    }

    if (typeof form.indication === "object") {
      if (form.indication?.label?.includes("Other")) {
        return `Other (${form?.indication_other})`;
      } else {
        return form?.indication?.label;
      }
    }
  };

  const displayConditionallyApprovedBy = () => {
    if (
      form?.status === "Approved" ||
      form?.status === "Rejected" ||
      form?.status === "Dispensed" ||
      form?.status === "Overdue"
    ) {
      return form?.approved_or_rejected_by;
    }

    if (
      form?.status === "Submitted" &&
      (isFormApproved === null || isFormApproved === undefined)
    ) {
      return "Awaiting approval";
    }

    if (
      form?.status === "Submitted" &&
      (isFormApproved !== null || isFormApproved !== undefined)
    ) {
      return storageData.first_name + " " + storageData.last_name;
    }
    return "Awaiting approval";
  };

  const displayConditionallyApprovedOrRejectedLabel = () => {
    if (form?.status === "Approved") {
      return "Approved By:";
    }

    if (form?.status === "Rejected") {
      return "Rejected By:";
    }

    if (form?.status === "Submitted" && isFormApproved === null) {
      return "Approved By:";
    }

    if (form?.status === "Submitted" && isFormApproved === true) {
      return "Approved By:";
    }

    if (form?.status === "Submitted" && isFormApproved === false) {
      return "Rejected By:";
    }

    return "Approved By:";
  };

  const displayConditionallyPrescriberName = () => {
    if (isCreateNewPAF) return prescriberName();
    if (
      form?.prescriber &&
      form?.prescriber?.first_name &&
      form?.prescriber?.last_name
    )
      return form?.prescriber?.first_name + " " + form?.prescriber?.last_name;
    return "";
  };

  const getPharmacyFromLocalStorage = () => {
    return storageData?.hospital?.value + ", " + storageData?.hospital?.address;
  };

  function typeOfForm() {
    const getTypeFromURL = window.location.pathname.split("/")[3];

    if (getTypeFromURL === "womanOfNCP") {
      return "Woman of Non-Childbearing Potential";
    } else if (getTypeFromURL === "male") {
      return "Male";
    } else {
      return "Woman of Childbearing Potential";
    }
  }

  const getPatientCategory = () => {
    // To check patient_type --> type ?
    if (!!form?.patient?.patient_type) {
      return form?.patient?.patient_type;
    } else if (!!form?.patient_type) {
      // Used for Print TIF
      return form.patient_type;
    } else if (!!form?.type) {
      switch (form?.type) {
        case "male":
          return "Male";
        case "womanOfNCP":
          return "Woman of Non-Childbearing Potential";
        case "womanOfCP":
          return "Woman of Childbearing Potential";
        default:
          return;
      }
    } else {
      // Used for Renew PAF
      return typeOfForm();
    }
  };

  const checkIfNumberOfCyclesExceeded = (form) => {
    const typeOfFormVar = getPatientCategory();

    if (typeOfFormVar === "Woman of Childbearing Potential") {
      let getNumberOfCyles;
      if (isObject(form.number_cycles)) {
        getNumberOfCyles = form.number_cycles.value;
      } else {
        getNumberOfCyles = getFirstNumberFromString(form.n_cycles);
      }

      return getNumberOfCyles > 1;
    }
  };

  const displayCheckBox = () => {
    return !!form.confirmation_risk_patient;
  };

  const disabledCheckBox = () => {
    if (isPharmacy) {
      // The PAF was Approved or Rejected
      if (isFormApproved !== null) {
        return true;
      } else if (
        // Prescriber checked the checkbox
        (form?.status === "Submitted" ||
          form?.status === "Overdue" ||
          form?.status === "Expiring") &&
        form.confirmation === false
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const displayLastNegativepregnanacyDate = () => {
    if (!!form?.pregnancy_test) {
      return moment(form?.pregnancy_test).format("DD / MM / YYYY");
    } else if (!!form?.patient?.pregnancy_test) {
      return moment(form?.patient?.pregnancy_test).format("DD / MM / YYYY");
    } else {
      moment().format("DD / MM / YYYY");
    }
  };

  const markInRedPrescribedOnField = (form) => {
    const typeOfFormVar = getPatientCategory();

    if (typeOfFormVar !== "Woman of Childbearing Potential") {
      return false;
    }

    if (form?.status === "Approved") {
      return isDispensingMoreThan7DaysBetween2Dates(
        form?.pfa_issued,
        form?.accepted_date
      );
    } else if (form?.status === "Rejected") {
      return isDispensingMoreThan7DaysBetween2Dates(
        form?.pfa_issued,
        form?.rejected_date
      );
    } else {
      const isCreate = window.location.pathname.includes("patient-initiation");
      const isRenewal = window.location.pathname.includes("renew");

      if (isCreate || isRenewal) {
        return false;
      } else {
        return isDispensingMoreThan7DaysBetween2Dates(
          form?.pfa_issued,
          new Date()
        );
      }
    }
  };

  const markInRedLastNegativePreganacyTest = (form) => {
    if (isCreateNewPAF) {
      return isDispensingMoreThan3DaysBetween2Dates(
        new Date(),
        form?.pregnancy_test
      );
    } else {
      return isDispensingMoreThan3DaysBetween2Dates(
        form?.pfa_issued,
        form?.pregnancy_test
      );
    }
  };

  const displayConditionallyCapsulesPerCycle = (property, otherProperty) => {
    if (isObject(form[property])) {
      if (form[property].value) {
        return form[property].value + " Capsules";
      }
      return form[otherProperty] + " Capsules";
    }
    if (!!form[otherProperty]) {
      return form[otherProperty] + " Capsules";
    }
    return form[property];
  };

  const showEditCapsule = (cycle, toShow) => {
    setEditCapsule({ ...editCapsule, [cycle]: toShow });
  };

  const handleOtherCapsChange = (e, capsule_cycle, other_caps) => {
    if (isPharmacy) {
      let amendFirst = false;
      const trackCapsuleChanges_copy =
        capsule_changes_tracker?.capsule_changes_tracker.map((element) => {
          const checkIncludesNewStrength = !!element.capsule_strength_view_new
            ? capsule_cycle.split("capsule_cycle_")[1] ===
              element.capsule_strength_view_new
            : !!element.capsule_strength_view_new;

          const existsANewCapsuleValue =
            capsule_changes_tracker?.capsule_changes_tracker?.find(
              (el) =>
                el.capsule_strength_view_new ===
                element.capsule_strength_view_initial
            );

          const has_initial_value =
            element.capsule_cycle_view_initial === e.target.value;

          if (
            element.capsule_cycle_view_initial === "Incomplete" &&
            existsANewCapsuleValue
          ) {
            amendFirst = true;
            return {
              ...element,
              capsule_cycle_view_new:
                e.target.value > 200 ? "200" : e.target.value,
            };
          } else {
            if (amendFirst) {
              return element;
            } else {
              if (checkIncludesNewStrength) {
                amendFirst = true;
                return {
                  ...element,
                  capsule_cycle_view_new: has_initial_value
                    ? ""
                    : e.target.value > 200
                    ? "200"
                    : e.target.value,
                };
              } else {
                if (amendFirst) {
                  return element;
                } else {
                  if (
                    capsule_cycle.split("capsule_cycle_")[1] ===
                      element.capsule_strength_view_initial &&
                    element.capsule_strength_view_new === "" &&
                    !existsANewCapsuleValue
                  ) {
                    return {
                      ...element,
                      capsule_cycle_view_new: has_initial_value
                        ? ""
                        : e.target.value > 200
                        ? "200"
                        : e.target.value,
                    };
                  } else {
                    return element;
                  }
                }
              }
            }
          }
        });

      setCapsule_changes_tracker({
        ...capsule_changes_tracker,
        capsule_changes_tracker: trackCapsuleChanges_copy,
      });
      // console.log(trackCapsuleChanges_copy, "new value other capsules");
    }

    e.preventDefault();
    let value = e?.target?.value;
    let parsedValue = parseInt(value);
    value = parsedValue ? parsedValue.toString() : value;
    let name = e?.target?.name;
    if (value < 1) value = 1;
    if (value > 200) value = 200;
    setFormItems({ ...form, [name]: value });
    handleChangeSubmitedForm("capsules_per_cycle", {
      e,
      capsule_cycle,
      other_caps,
    });
  };

  const handleCapsuleCycleEditChange = (capsule_cycle, other_caps, e) => {
    if (isPharmacy) {
      let amendFirst = false;

      const trackCapsuleChanges_copy =
        capsule_changes_tracker.capsule_changes_tracker.map((element) => {
          const has_initial_value =
            element.capsule_cycle_view_initial === e.label?.split(" ")[0] ||
            ("Other (please state)".includes(e.label) &&
              element.capsule_cycle_view_initial === "1 Capsules");

          const checkIncludesNewStrength = !!element.capsule_strength_view_new
            ? capsule_cycle.split("capsule_cycle_")[1] ===
              element.capsule_strength_view_new
            : !!element.capsule_strength_view_new;

          const existsANewCapsuleValue =
            capsule_changes_tracker?.capsule_changes_tracker?.find(
              (el) =>
                el.capsule_strength_view_new ===
                element.capsule_strength_view_initial
            );

          if (
            element.capsule_cycle_view_initial === "Incomplete" &&
            existsANewCapsuleValue
          ) {
            amendFirst = true;
            return {
              ...element,
              capsule_cycle_view_new: "Other (please state)".includes(e.label)
                ? "1"
                : e.label?.split(" ")[0],
            };
          } else {
            if (amendFirst) {
              return element;
            } else {
              if (checkIncludesNewStrength) {
                amendFirst = true;
                return {
                  ...element,
                  capsule_cycle_view_new: has_initial_value
                    ? ""
                    : "Other (please state)".includes(e.label)
                    ? "1"
                    : e.label?.split(" ")[0],
                };
              } else {
                if (amendFirst) {
                  return element;
                } else {
                  if (
                    capsule_cycle.split("capsule_cycle_")[1] ===
                      element.capsule_strength_view_initial &&
                    element.capsule_strength_view_new === "" &&
                    !existsANewCapsuleValue
                  ) {
                    return {
                      ...element,
                      capsule_cycle_view_new: has_initial_value
                        ? ""
                        : "Other (please state)".includes(e.label)
                        ? "1"
                        : e.label?.split(" ")[0],
                    };
                  } else {
                    return element;
                  }
                }
              }
            }
          }
        });

      setCapsule_changes_tracker({
        ...capsule_changes_tracker,
        capsule_changes_tracker: trackCapsuleChanges_copy,
      });
      // console.log(trackCapsuleChanges_copy, "new value capsules");
    }

    const { label } = e;
    let newForm = { ...form };

    handleAditionalOptionsChange(e, "select", "capsulePerCycle");

    if (label?.includes("Other")) {
      setFormItems({
        ...newForm,
        [other_caps]: newForm[other_caps] ?? 1,
        [capsule_cycle]: 0,
      });
      showEditCapsule(other_caps, true);
      return;
    } else {
      delete newForm[other_caps];
      showEditCapsule(other_caps, false);
    }
    newForm[capsule_cycle] = label;
    setFormItems(newForm);
    handleChangeSubmitedForm("capsules_per_cycle", {
      e,
      capsule_cycle,
      other_caps,
    });
    // Possible fix for Capsule Per Cycle when selecting an option different then Other to close the select and keep text
    setEditCapsule({});
  };

  // This closes the edit for capsules per cycle after a value is selected
  // useEffect(() => {
  //     let editCapsAux = { ...editCapsule };
  //     Object.keys(editCapsAux).forEach((key) => {
  //       editCapsAux[key] = false;
  //     });
  //     setEditCapsule(editCapsAux);
  // }, [form]);

  const handleEditCapsStrength = (whichStrength, _e) => {
    setEditedCapsStrength({ [whichStrength]: true });
  };

  const buildCapsuleName = (mg, other = false) => {
    if (mg?.toString().includes(".")) {
      mg = mg.toString().split(".").join("_");
    }
    if (other) {
      return `capsule_cycle_other_${mg}`;
    }
    return `capsule_cycle_${mg}`;
  };

  const handleCapsuleStrEditInvalid = (oldStrength, e) => {
    let newForm = { ...form };

    const capsule_changes_tracker_copy =
      capsule_changes_tracker?.capsule_changes_tracker.map((element) => {
        if (element.capsule_strength_view_initial === "Incomplete") {
          return {
            ...element,
            capsule_strength_view_new: e.value.toString().replace(".", "_"),
          };
        }
        return element;
      });

    setCapsule_changes_tracker({
      ...capsule_changes_tracker,
      capsule_changes_tracker: capsule_changes_tracker_copy,
    });

    // console.log(capsule_changes_tracker_copy, "capsule_changes_tracker_copy");

    // TODO: Add condition to execute once
    handleAditionalOptionsChange(e, "select", "capsuleStrength");

    let capsuleValue = e.value.toString();
    if (capsuleValue.includes(".")) {
      capsuleValue = capsuleValue.replace(".", "_");
    }

    newForm[`capsule_cycle_${capsuleValue}`] = oldStrength;

    setFormItems({ ...newForm });
  };

  const handleCapsuleStrEditChange = (oldStrength, e) => {
    if (isPharmacy) {
      let amendFirst = false;
      const trackCapsuleChanges_copy =
        capsule_changes_tracker?.capsule_changes_tracker.map((element) => {
          const has_initial_value =
            element.capsule_strength_view_initial ===
            e.value.toString().replace(".", "_");

          const existsANewCapsuleValue =
            capsule_changes_tracker?.capsule_changes_tracker?.filter(
              (element) => {
                return (
                  element.capsule_strength_view_new.replace(".", "_") ===
                  oldStrength
                );
              }
            );

          if (
            existsANewCapsuleValue.length > 0 &&
            element.capsule_strength_view_initial === "Incomplete" &&
            existsANewCapsuleValue[0].capsule_strength_view_initial ===
              "Incomplete"
          ) {
            amendFirst = true;
            return {
              ...element,
              capsule_strength_view_new: e.value.toString().replace(".", "_"),
            };
          } else {
            if (amendFirst) {
              return element;
            } else {
              // It was amend once capsule_strength_view_new
              if (element.capsule_strength_view_new === oldStrength) {
                amendFirst = true;
                return {
                  ...element,
                  capsule_strength_view_new: has_initial_value
                    ? ""
                    : e.value.toString().replace(".", "_"),
                };
              }
              if (amendFirst) {
                return element;
              } else {
                if (
                  element.capsule_strength_view_initial === oldStrength &&
                  element.capsule_strength_view_new === "" &&
                  !existsANewCapsuleValue.length > 0 // ?
                ) {
                  return {
                    ...element,
                    capsule_strength_view_new: has_initial_value
                      ? ""
                      : e.value.toString().replace(".", "_"),
                  };
                } else {
                  return element;
                }
              }
            }
          }
        });

      setCapsule_changes_tracker({
        ...capsule_changes_tracker,
        capsule_changes_tracker: trackCapsuleChanges_copy,
      });
      // console.log(
      //   trackCapsuleChanges_copy,
      //   "new value capsule strength changed"
      // );
    }

    const { value } = e;
    const prevCaps = buildCapsuleName(oldStrength);
    const prevCapsOther = buildCapsuleName(oldStrength, true);
    let newForm = { ...form };
    newForm[buildCapsuleName(value)] = newForm[prevCaps];
    newForm[buildCapsuleName(value, true)] = newForm[prevCapsOther];

    const oldValue = oldStrength.includes("_")
      ? +oldStrength.replace("_", ".")
      : +oldStrength;

    if (value !== oldValue) {
      delete newForm[prevCaps];
      delete newForm[prevCapsOther];
    }

    setFormItems({ ...newForm });
    setEditedCapsStrength({});
    setEditCapsule({});
    handleChangeSubmitedForm("capsule_strength", { oldStrength, e });
  };

  const displayEditCapsulePerCycle = (capsule_cycle) => {
    return (
      (form?.status === "Submitted" ||
        form?.status === "Overdue" ||
        form?.status === "Expiring") &&
      isPharmacy && (
        <React.Fragment>
          {isFormApproved === null && (
            <span
              className="edit-option"
              onClick={() => showEditCapsule(capsule_cycle, true)}
            >
              EDIT
            </span>
          )}
        </React.Fragment>
      )
    );
  };

  const deleteProperty = (propName) => {
    deletePropertyValue(propName);
  };

  const isNotSelectedAnyCapsulePerCycle = () => {
    return !(
      !!form?.capsule_cycle_other_2_5 ||
      !!form?.capsule_cycle_2_5 ||
      !!form?.capsule_cycle_other_5 ||
      !!form?.capsule_cycle_5 ||
      !!form?.capsule_cycle_other_7_5 ||
      !!form?.capsule_cycle_7_5 ||
      !!form?.capsule_cycle_other_10 ||
      !!form?.capsule_cycle_10 ||
      !!form?.capsule_cycle_other_15 ||
      !!form?.capsule_cycle_15 ||
      !!form?.capsule_cycle_other_20 ||
      !!form?.capsule_cycle_20 ||
      !!form?.capsule_cycle_other_25 ||
      !!form?.capsule_cycle_25
    );
  };

  const isInvalidForCapsuleCycle = (cycle, other_cycle) => {
    const condition =
      (form[cycle] === "Incomplete" ||
        form[cycle] === null ||
        form[cycle] === undefined) &&
      (form[other_cycle] === "Incomplete" ||
        form[other_cycle] === null ||
        form[other_cycle] === undefined);

    return condition;
  };

  // Used at Create PAF
  const handedHospitalName =
    storageData?.hospital?.value + ", " + storageData?.hospital?.address;

  const capsule_tracker_object = (value) => {
    const match_with_capsule_strength_new =
      capsule_changes_tracker?.capsule_changes_tracker?.find((element) => {
        return element.capsule_strength_view_new === value;
      });

    const match_with_capsule_strength_old =
      capsule_changes_tracker?.capsule_changes_tracker?.find((element) => {
        return element.capsule_strength_view_initial === value;
      });

    return match_with_capsule_strength_new || match_with_capsule_strength_old;
  };

  const is_preganacy_test_changes = !!capsule_changes_tracker?.pregnancy_test;

  return form ? (
    <Layout>
      <h2 className="mineShaft-color">Patient Details</h2>
      <div className="container patients-wrapper-container">
        <div className="row">
          <div className="col">
            <label>Patient Category:</label>
            <div className="font-size-16">{getPatientCategory()}</div>
          </div>
          <div className="col" />
        </div>
        <div className="row">
          <div className="col">
            <label>Indication:</label>
            <div
              className={`font-size-16 ${
                extractIndication()?.toLowerCase()?.includes("other") &&
                (isPrescriber || isConnectedNurse)
                  ? "red-label"
                  : ""
              }`}
            >
              {extractIndication() || (
                <div className="red-label font-size-16">Incomplete</div>
              )}
            </div>
          </div>
          <div className="col">
            <label>Molecule:</label>
            <div className="font-size-16">{form?.drug ?? form?.product}</div>
          </div>
          {getPatientCategory() === "Woman of Childbearing Potential" && (
            <div className="col">
              <label>Last Negative Pregnancy Test:</label>
              <div
                className={
                  "font-size-16 display-flex-tooltip " +
                  (markInRedLastNegativePreganacyTest(form)
                    ? "red-label"
                    : "") +
                  (!isCreateNewPAF &&
                  !is_form_ready_form_approve &&
                  is_preganacy_test_changes
                    ? " green-haze"
                    : "")
                }
              >
                {displayLastNegativepregnanacyDate() || (
                  <span className="red-label font-size-16">Incomplete</span>
                )}
                {!isCreateNewPAF &&
                  !is_form_ready_form_approve &&
                  is_preganacy_test_changes && (
                    <ToolTipHover
                      capsule_changes_tracker={{
                        pregnanacy_test_view_initial:
                          capsule_changes_tracker?.pregnancy_test,
                        pregnanacy_test_view_new: form?.pregnancy_test,
                      }}
                      changedBy={form.approved_or_rejected_by}
                      changedOn={form.accepted_date}
                      typeOfField="date"
                    />
                  )}

                {(form?.status === "Submitted" ||
                  form?.status === "Overdue" ||
                  form?.status === "Expiring") &&
                  isPharmacy && (
                    <React.Fragment>
                      {isFormApproved === null && (
                        <span
                          className="edit-option"
                          onClick={(event) =>
                            editField(event, "lastNegativePregnanacyTest")
                          }
                        >
                          EDIT
                        </span>
                      )}
                    </React.Fragment>
                  )}
              </div>
              {(form?.status === "Submitted" ||
                form?.status === "Overdue" ||
                form?.status === "Expiring") &&
                isLastPregancyTestActive === true && (
                  <CalendarComponent
                    close={setIsLastPregancyTestActive}
                    onClose={() => setIsLastPregancyTestActive(false)}
                    selectRange={false}
                    maxDate={new Date()}
                    setDateRaw={(date) => {
                      handleChangeSubmitedForm("pregnancy_test", date);
                    }}
                    date={
                      form?.patient?.pregnancy_test || form?.pregnancy_test
                        ? moment(
                            form?.patient?.pregnancy_test ||
                              form?.pregnancy_test
                          ).toDate()
                        : moment(new Date()).toDate()
                    }
                    form={form}
                    onResetCalendar={() => {
                      deleteProperty("pregnancy_test");
                    }}
                  />
                )}
            </div>
          )}
          <div className="col">
            <label>Date of PAF:</label>
            {isPrescribedOn === false && (
              <div
                className={
                  "font-size-16 " +
                  (markInRedPrescribedOnField(form) ? "red-label" : "")
                }
              >
                {isCreateNewPAF
                  ? moment(new Date()).format("DD / MM / YYYY")
                  : moment(form?.pfa_issued).format("DD / MM / YYYY")}{" "}
              </div>
            )}
            {form?.status === "Submitted" && isPrescribedOn === true && (
              <CalendarComponent
                close={setIsPrescribedOn}
                selectRange={false}
                maxDate={new Date()}
                setDateRaw={(date) => {
                  handleChangeSubmitedForm("prescribed_on", date);
                }}
                date={new Date(form?.pfa_issued)}
              />
            )}
          </div>

          {getPatientCategory() !== "Woman of Childbearing Potential" && (
            <div className="col" />
          )}
        </div>
      </div>

      {getPatientCategory() !== "Woman of Non-Childbearing Potential" && (
        <label
          htmlFor="confirmation"
          className={
            "custom-checkbox font-size-16 mt-4 mb-4 " +
            (!displayCheckBox() ? "red-label" : "")
          }
        >
          {getPatientCategory() === "Male"
            ? `The patient has been counselled about the teratogenic risk of treatment with lenalidomide and understands the need to use a condom if involved in sexual activity with a woman of childbearing potential not using effective contraception or if their partner is pregnant (even if the patient has had a vasectomy).`
            : `The patient has been counselled about the teratogenic risk of treatment, the need to avoid pregnancy, and has been on effective contraception for at least 4 weeks or committed to absolute and continuous abstinence confirmed on a monthly basis.`}

          <input
            type="checkbox"
            id="confirmation"
            name="confirmation"
            checked={displayCheckBox()}
            onChange={
              (form?.status === "Submitted" ||
                form?.status === "Overdue" ||
                form?.status === "Expiring") &&
              isPharmacy
                ? (event) => handleChangeSubmitedForm("confirmation", event)
                : () => true
            }
            disabled={disabledCheckBox()}
          />
          <span className="checkmark" />
        </label>
      )}

      <hr className="gray-lightE8-hr" />

      <div className="container patients-wrapper-container mb-2">
        <div className="row">
          <div className="col">
            <label
              className={
                aditionalOptionsState?.showErrors &&
                !aditionalOptionsState?.validation?.capsuleStrength?.isValid
                  ? "error"
                  : ""
              }
            >
              {disableAditionalOptions()
                ? "Capsule Strength:"
                : "Capsule Strength*:"}
            </label>
          </div>
          <div className="col">
            <label
              className={
                aditionalOptionsState?.showErrors &&
                !aditionalOptionsState?.validation?.capsulePerCycle?.isValid
                  ? "error"
                  : ""
              }
            >
              {disableAditionalOptions()
                ? "Capsules per Cycle:"
                : "Capsules per Cycle*:"}
            </label>
          </div>
          <div className="col">
            <label
              className={
                aditionalOptionsState?.showErrors &&
                !aditionalOptionsState?.validation?.numberOfCycles?.isValid
                  ? "error"
                  : ""
              }
            >
              {disableAditionalOptions()
                ? "Number of Cycles:"
                : "Number of Cycles*:"}
            </label>
          </div>
          <div className="col">
            <label>Total Capsules:</label>
          </div>
        </div>

        {isNotSelectedAnyCapsulePerCycle() && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"Incomplete"}
              handleCapsuleStrEditInvalid={handleCapsuleStrEditInvalid}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div className="col red-label font-size-16">Incomplete</div>
            <div className="col font-size-16">
              {form?.number_cycles?.label || form?.n_cycles || (
                <span className="col red-label font-size-16">Incomplete</span>
              )}
            </div>
            <div className="col font-size-16">
              {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
            </div>
          </div>
        )}

        {(!!form?.capsule_cycle_other_2_5 || !!form?.capsule_cycle_2_5) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"2.5mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_2_5"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_2_5"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16" +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_2_5",
                      "capsule_cycle_other_2_5"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("2_5")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_2_5"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_2_5",
                      "capsule_cycle_other_2_5"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_2_5"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("2_5")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("2_5")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}
              <EditCapsuleCycle
                capsule={"capsule_cycle_2_5"}
                capsule_other={"capsule_cycle_other_2_5"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_2_5")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_2_5" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_5 || !!form?.capsule_cycle_5) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"5mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_5"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_5"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_5",
                      "capsule_cycle_other_5"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("5")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_5"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_5",
                      "capsule_cycle_other_5"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_5"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("5")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("5")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}
              <EditCapsuleCycle
                capsule={"capsule_cycle_5"}
                capsule_other={"capsule_cycle_other_5"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col display-flex-tooltip">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_5")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_5" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_7_5 || !!form?.capsule_cycle_7_5) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"7.5mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_7_5"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_7_5"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_7_5",
                      "capsule_cycle_other_7_5"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("7_5")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_7_5"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_7_5",
                      "capsule_cycle_other_7_5"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_7_5"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("7_5")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("7_5")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}

              <EditCapsuleCycle
                capsule={"capsule_cycle_7_5"}
                capsule_other={"capsule_cycle_other_7_5"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col display-flex-tooltip">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_7_5")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_7_5" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_10 || !!form?.capsule_cycle_10) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"10mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_10"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_10"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_10",
                      "capsule_cycle_other_10"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("10")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_10"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_10",
                      "capsule_cycle_other_10"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_10"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("10")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("10")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}

              <EditCapsuleCycle
                capsule={"capsule_cycle_10"}
                capsule_other={"capsule_cycle_other_10"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_10")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_10" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_15 || !!form?.capsule_cycle_15) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"15mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_15"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_15"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_15",
                      "capsule_cycle_other_15"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("15")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_15"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_15",
                      "capsule_cycle_other_15"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_15"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("15")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("15")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}
              <EditCapsuleCycle
                capsule={"capsule_cycle_15"}
                capsule_other={"capsule_cycle_other_15"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_15")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_15" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_20 || !!form?.capsule_cycle_20) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"20mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_20"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_20"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_20",
                      "capsule_cycle_other_20"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("20")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_20"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_20",
                      "capsule_cycle_other_20"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_20"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("20")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("20")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}
              <EditCapsuleCycle
                capsule={"capsule_cycle_20"}
                capsule_other={"capsule_cycle_other_20"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_20")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_20" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
        {(!!form?.capsule_cycle_other_25 || !!form?.capsule_cycle_25) && (
          <div className="row">
            <EditCapsuleStrength
              form={form}
              isPharmacy={isPharmacy}
              editedCapsStrength={editedCapsStrength}
              handleEditCapsStrength={handleEditCapsStrength}
              handleCapsuleStrEditChange={handleCapsuleStrEditChange}
              DropdownIndicator={DropdownIndicator}
              isFormApproved={isFormApproved}
              mgFor={"25mg"}
              capsule_changes_tracker={
                capsule_changes_tracker?.capsule_changes_tracker
              }
            />
            <div
              className={
                "col " +
                (editCapsule["capsule_cycle_25"] !== true &&
                  "display-flex-tooltip")
              }
            >
              {editCapsule["capsule_cycle_25"] !== true ? (
                <span
                  className={
                    "tool-tip-wrapper-capsule-per-cycle font-size-16 " +
                    (isInvalidForCapsuleCycle(
                      "capsule_cycle_25",
                      "capsule_cycle_other_25"
                    )
                      ? "red-label"
                      : "") +
                    (!isCreateNewPAF &&
                    !is_form_ready_form_approve &&
                    !!capsule_tracker_object("25")?.capsule_cycle_view_new
                      ? " green-haze"
                      : "")
                  }
                >
                  {editCapsule["capsule_cycle_25"] !== true &&
                    displayConditionallyCapsulesPerCycle(
                      "capsule_cycle_25",
                      "capsule_cycle_other_25"
                    )}
                </span>
              ) : (
                ""
              )}
              {editCapsule["capsule_cycle_25"] !== true &&
                !isCreateNewPAF &&
                !is_form_ready_form_approve &&
                !!capsule_tracker_object("25")?.capsule_cycle_view_new && (
                  <ToolTipHover
                    capsule_changes_tracker={capsule_tracker_object("25")}
                    changedBy={form.approved_or_rejected_by}
                    changedOn={form.accepted_date}
                    typeOfField="capsule_cycle"
                  />
                )}
              <EditCapsuleCycle
                capsule={"capsule_cycle_25"}
                capsule_other={"capsule_cycle_other_25"}
                editCapsule={editCapsule}
                displayEditCapsulePerCycle={displayEditCapsulePerCycle}
                handleOtherCapsChange={handleOtherCapsChange}
                DropdownIndicator={DropdownIndicator}
                handleCapsuleCycleEditChange={handleCapsuleCycleEditChange}
                form={form}
                capsulePerCycle={capsulePerCycle}
              />
            </div>
            <div className="col">
              <div className="font-size-16 display-flex-tooltip selectWidth ">
                {displayConditionallyNumberOfCycles("capsule_cycle_25")}
              </div>
            </div>
            <div className="col">
              {isFirst === "capsule_cycle_25" && (
                <div className="font-size-16">
                  {calculateTotalCaps(form) ?? form?.total_capsules} Capsules
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Capsule Strength</th>
              <th>2.5mg</th>
              <th>5mg</th>
              <th>7.5mg</th>
              <th>10mg</th>
              <th>15mg</th>
              <th>20mg</th>
              <th>25mg</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Capsule Quantity</td>
              <td>
                {!!form.capsule_cycle_2_5 || !!form.capsule_cycle_other_2_5
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_2_5",
                      "capsule_cycle_other_2_5"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_5 || !!form.capsule_cycle_other_5
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_5",
                      "capsule_cycle_other_5"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_7_5 || !!form.capsule_cycle_other_7_5
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_7_5",
                      "capsule_cycle_other_7_5"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_10 || !!form.capsule_cycle_other_10
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_10",
                      "capsule_cycle_other_10"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_15 || !!form.capsule_cycle_other_15
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_15",
                      "capsule_cycle_other_15"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_20 || !!form.capsule_cycle_other_20
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_20",
                      "capsule_cycle_other_20"
                    )
                  : ""}
              </td>
              <td>
                {!!form.capsule_cycle_25 || !!form.capsule_cycle_other_25
                  ? calculateCapsuleQuantity(
                      "capsule_cycle_25",
                      "capsule_cycle_other_25"
                    )
                  : ""}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <hr className="gray-lightE8-hr" />

      <div className="container patients-wrapper-container">
        <div className="row">
          <div className="col">
            <label>Prescriber:</label>
            <div className="font-size-16">
              {displayConditionallyPrescriberName()}
            </div>
          </div>
          <div className="col">
            <label>Hospital Address:</label>
            <div className="font-size-16">
              {form?.hospital_name_and_address ||
                form?.patient?.hospital ||
                handedHospitalName}
            </div>
          </div>
          <div className="col"></div>
        </div>
        <div className="row">
          <div className="col">
            <label>{displayConditionallyApprovedOrRejectedLabel()}</label>
            <div className="font-size-16">
              {displayConditionallyApprovedBy()}
            </div>
          </div>
          <div className="col">
            <label>Pharmacy Address:</label>
            <div className="font-size-16">
              {dispensingForm?.location?.address ||
                form?.pharmacy_address ||
                form?.patient?.pharmacy ||
                getPharmacyFromLocalStorage()}
            </div>
          </div>
          <div className="col"></div>
        </div>

        {form.status !== "Rejected" ? (
          <>
            {!isPatientInitiationOrRenewPAF && (
              <div className="row">
                <div className="col">
                  <label
                    className={
                      aditionalOptionsState?.showErrors &&
                      !aditionalOptionsState?.validation?.number_of_packs
                        ?.isValid
                        ? "error"
                        : ""
                    }
                  >
                    {disableAditionalOptions() ? "Pack Type:" : "Pack Type*:"}
                  </label>
                  {!disableAditionalOptions() ? (
                    <form autoComplete="off">
                      <Select
                        menuPosition="fixed"
                        classNamePrefix="custom-select"
                        components={{
                          DropdownIndicator: DropdownIndicator,
                          IndicatorSeparator: () => null,
                        }}
                        menuShouldScrollIntoView={true}
                        styles={customStyles}
                        options={typeOfPacks}
                        value={aditionalOptions?.number_of_packs}
                        name="number_of_packs"
                        onChange={(e) => {
                          handleAditionalOptionsChange(
                            e,
                            "select",
                            "number_of_packs"
                          );
                        }}
                        placeholder=""
                        className={
                          "select-border " +
                          (aditionalOptionsState?.showErrors &&
                          !aditionalOptionsState?.validation?.number_of_packs
                            ?.isValid
                            ? "error-border"
                            : "")
                        }
                      />
                    </form>
                  ) : (
                    <div className="font-size-16">
                      <span className={"font-size-16"}>
                        {aditionalOptions?.number_of_packs?.label ||
                          receivedForm?.number_of_packs_label ||
                          "Awaiting Approval"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="col flex-center">
                  <label
                    htmlFor="private_patient"
                    className={
                      "custom-checkbox treated-checkbox font-size-16 mt-4 mb-4 " +
                      (aditionalOptionsState?.showErrors &&
                      !aditionalOptionsState?.validation?.patient?.isValid
                        ? "checkmark-error"
                        : "")
                    }
                  >
                    Private Patient
                    <input
                      type="checkbox"
                      id="private_patient"
                      name="private_patient"
                      checked={
                        aditionalOptions?.private_patient ||
                        receivedForm?.private_patient ||
                        false
                      }
                      value={aditionalOptions?.private_patient || false}
                      onChange={handleAditionalOptionsChange}
                      disabled={disableAditionalOptions()}
                    />
                    <span
                      className={
                        "checkmark " +
                        (aditionalOptionsState?.showErrors &&
                        !aditionalOptionsState?.validation.patient?.isValid
                          ? "checkmark-error"
                          : "")
                      }
                    />
                  </label>
                </div>
                <div className="col flex-center">
                  <label
                    htmlFor="nhs_patient"
                    className={
                      "custom-checkbox treated-checkbox font-size-16 mt-4 mb-4 " +
                      +(aditionalOptionsState?.showErrors &&
                      !aditionalOptionsState?.validation?.patient?.isValid
                        ? "error"
                        : "")
                    }
                  >
                    NHS Patient
                    <input
                      type="checkbox"
                      id="nhs_patient"
                      name="nhs_patient"
                      checked={
                        aditionalOptions?.nhs_patient ||
                        receivedForm?.nhs_patient ||
                        false
                      }
                      value={aditionalOptions?.nhs_patient || false}
                      onChange={handleAditionalOptionsChange}
                      disabled={disableAditionalOptions()}
                    />
                    <span
                      className={
                        "checkmark " +
                        (aditionalOptionsState?.showErrors &&
                        !aditionalOptionsState?.validation?.patient?.isValid
                          ? "checkmark-error"
                          : "")
                      }
                    />
                  </label>
                </div>
                <div className="col">
                  <label>Brand selected:</label>
                  <div className="font-size-16">
                    {form?.marketing_holder || "Awaiting to be Dispensed"}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div />
        )}
      </div>
    </Layout>
  ) : (
    ""
  );
};

ViewPAF.propTypes = {
  receivedForm: PropTypes.object,
  handleChangeSubmitedForm: PropTypes.func,
  numberOfCylesValues: PropTypes.array,
  isFormApproved: PropTypes.bool,
  isCreateNewPAF: PropTypes.bool,
  handleAditionalOptionsChange: PropTypes.func,
  aditionalOptions: PropTypes.object,
  typeOfPacks: PropTypes.array,
  aditionalOptionsState: PropTypes.object,
  setFormItems: PropTypes.func,
  dispensingForm: PropTypes.object,
};

export default ViewPAF;
